import React, { useState, useEffect } from 'react';
import VoiceAnalytics from '../voice/VoiceAnalytics';
import { MessageSquare, Calendar, TrendingUp, Users, Clock, CheckCircle, MousePointer } from 'lucide-react';

const MetricCard = ({ title, value, subtext, icon: Icon, colorClass, bgClass }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex items-start justify-between">
        <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
            <h3 className="text-2xl font-bold text-slate-900">{value}</h3>
            {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-lg ${bgClass}`}>
            <Icon className={`w-5 h-5 ${colorClass}`} />
        </div>
    </div>
);

const SectionHeader = ({ title, icon: Icon }) => (
    <div className="flex items-center gap-2 mb-4 mt-8">
        <div className="h-8 w-1 bg-blue-600 rounded-full"></div>
        {Icon && <Icon className="w-5 h-5 text-slate-700" />}
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
    </div>
);

const UnifiedAnalytics = () => {
    // --- Mock Data for New Sections ---
    // In a real app, these would come from apiService.getChatStats() etc.
    const chatStats = {
        active: 3,
        totalToday: 45,
        avgResponse: '1m 20s',
        satisfaction: '4.9/5'
    };

    const calendarStats = {
        booked: 8,
        views: 156,
        conversion: '5.1%'
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

            {/* 1. Voice Analytics (Existing Robust Component) */}
            <div>
                {/* VoiceAnalytics has its own header, so we just wrap it */}
                <VoiceAnalytics />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* 2. Chat Analytics */}
                <div className="space-y-4">
                    <SectionHeader title="Live Chat Performance" icon={MessageSquare} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <MetricCard
                            title="Active Chats"
                            value={chatStats.active}
                            subtext="Currently Online"
                            icon={Users}
                            colorClass="text-blue-600"
                            bgClass="bg-blue-50"
                        />
                        <MetricCard
                            title="Total Chats"
                            value={chatStats.totalToday}
                            subtext="Last 24 Hours"
                            icon={MessageSquare}
                            colorClass="text-indigo-600"
                            bgClass="bg-indigo-50"
                        />
                        <MetricCard
                            title="Avg Response"
                            value={chatStats.avgResponse}
                            subtext="Machine Speed"
                            icon={Clock}
                            colorClass="text-emerald-600"
                            bgClass="bg-emerald-50"
                        />
                        <MetricCard
                            title="CSAT Score"
                            value={chatStats.satisfaction}
                            subtext="Customer Rating"
                            icon={CheckCircle}
                            colorClass="text-amber-600"
                            bgClass="bg-amber-50"
                        />
                    </div>
                </div>

                {/* 3. External Calendar Analytics */}
                <div className="space-y-4">
                    <SectionHeader title="Calendar Widget Stats" icon={Calendar} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <MetricCard
                            title="Appointments"
                            value={calendarStats.booked}
                            subtext="Booked Today"
                            icon={Calendar}
                            colorClass="text-purple-600"
                            bgClass="bg-purple-50"
                        />
                        <MetricCard
                            title="Widget Views"
                            value={calendarStats.views}
                            subtext="Unique Loads"
                            icon={MousePointer}
                            colorClass="text-sky-600"
                            bgClass="bg-sky-50"
                        />
                        <MetricCard
                            title="Conversion"
                            value={calendarStats.conversion}
                            subtext="View to Book Rate"
                            icon={TrendingUp}
                            colorClass="text-rose-600"
                            bgClass="bg-rose-50"
                        />
                        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-5 flex items-center justify-center text-slate-400 text-sm">
                            More metrics coming soon...
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnifiedAnalytics;
